import { LESSON_EDITION_STATUSES, LESSON_LIST_STATUSES } from '@didaskill/domain/lessons/constants';
import type { LessonViewInternal, LessonViewList } from '@didaskill/domain/lessons/types';
import type { VersionObjectBase } from '@didaskill/domain/platform-types';
import { serializeVersion } from '@didaskill/domain/versions';

/** Version fields used to resolve the lessons-list version and its update date. */
type LessonListVersionCandidate = VersionObjectBase<false> & {
  id?: string;
  updatedAt?: string;
};

/** Lesson fields used to resolve published or displayed version numbers. */
type LessonListVersionSource = {
  versions?: LessonListVersionCandidate[];
  editableVersions?: LessonListVersionCandidate[];
};

/**
 * Compares two version numbers by major, then minor.
 * A missing minor is treated as 0.
 * @param versionA First version.
 * @param versionB Second version.
 * @returns A negative number when `versionA` is older, positive when it is newer, 0 when equal.
 */
function compareVersionNumbers(
  versionA: VersionObjectBase<false>,
  versionB: VersionObjectBase<false>,
): number {
  if (versionA.versionMajor !== versionB.versionMajor) {
    return versionA.versionMajor - versionB.versionMajor;
  }

  return (versionA.versionMinor ?? 0) - (versionB.versionMinor ?? 0);
}

/**
 * Returns the version with the highest major, then minor number.
 * Stored arrays are not ordered by version number, so the last element is not reliable.
 * @param versions Versions to compare.
 * @returns The highest version, or null when the list is empty.
 */
export function findHighestVersion<T extends VersionObjectBase<false>>(
  versions?: T[] | null,
): T | null {
  const versionList = versions ?? [];
  const [firstVersion] = versionList;

  if (!firstVersion) {
    return null;
  }

  let highestVersion = firstVersion;

  for (const version of versionList) {
    if (compareVersionNumbers(version, highestVersion) > 0) {
      highestVersion = version;
    }
  }

  return highestVersion;
}

/**
 * Returns the version shown in the lessons list.
 * Uses the highest version number among drafts and published versions.
 * On a tie, the draft wins.
 * @param lesson Lesson with optional editable and published versions.
 * @returns Displayed version, or null when the lesson has no version yet.
 */
function resolveDisplayedVersion<T extends VersionObjectBase<false>>(lesson: {
  versions?: T[];
  editableVersions?: T[];
}): T | null {
  const latestEditableVersion = findHighestVersion(lesson.editableVersions);
  const latestPublishedVersion = findHighestVersion(lesson.versions);

  if (!latestEditableVersion) {
    return latestPublishedVersion;
  }

  if (
    !latestPublishedVersion ||
    compareVersionNumbers(latestEditableVersion, latestPublishedVersion) >= 0
  ) {
    return latestEditableVersion;
  }

  return latestPublishedVersion;
}

/**
 * Returns a copy of the version number fields used by list and PDF helpers.
 * @param version Source version.
 * @returns Major and minor version numbers.
 */
function toVersionNumber(version: VersionObjectBase<false>): VersionObjectBase<false> {
  return {
    versionMajor: version.versionMajor,
    versionMinor: version.versionMinor,
  };
}

/** Lesson fields used to resolve the list-view "updated at" date. */
type LessonListUpdatedAtSource = LessonListVersionSource & {
  updatedAt: string;
};

/**
 * Returns the number of skills linked to a lesson, regardless of the view shape.
 * @param lesson Lesson from the list or internal view.
 * @returns Skills count.
 */
export function getLessonSkillsCount(lesson: LessonViewInternal | LessonViewList): number {
  if ('skillsCount' in lesson && typeof lesson.skillsCount === 'number') {
    return lesson.skillsCount;
  }

  if ('skills' in lesson) {
    return lesson.skills.length;
  }

  return 0;
}

/**
 * Returns the number of lesson responsibles, regardless of the view shape.
 * @param lesson Lesson from the list or internal view.
 * @returns Responsibles count.
 */
export function getLessonResponsiblesCount(lesson: LessonViewInternal | LessonViewList): number {
  if ('responsiblesCount' in lesson && typeof lesson.responsiblesCount === 'number') {
    return lesson.responsiblesCount;
  }

  if ('responsibles' in lesson) {
    return lesson.responsibles.length;
  }

  return 0;
}

/**
 * Returns the precomputed ongoing-alerts count when available (list view).
 * @param lesson Lesson from the list or internal view.
 * @returns The count, or undefined when the payload has no precomputed value.
 */
export function getLessonListOngoingAlertsCount(
  lesson: LessonViewInternal | LessonViewList,
): number | undefined {
  if ('ongoingAlertsCount' in lesson && typeof lesson.ongoingAlertsCount === 'number') {
    return lesson.ongoingAlertsCount;
  }

  return undefined;
}

/**
 * Returns the last-update date shown in the lessons list.
 * Uses the date of the highest version number, then the lesson document.
 * @param lesson Lesson with optional version timestamps.
 * @returns ISO date of the displayed version, or the lesson timestamp as fallback.
 */
export function getLessonListUpdatedAt(lesson: LessonListUpdatedAtSource): string {
  const displayedVersion = resolveDisplayedVersion(lesson);

  if (displayedVersion?.updatedAt) {
    return displayedVersion.updatedAt;
  }

  return lesson.updatedAt;
}

/** Lesson fields used to resolve the list-view edition status. */
type LessonListStatusSource = {
  hasVersionAwaitingValidation?: boolean;
  hasVersionEditing?: boolean;
  hasEditableVersions?: boolean;
  editableVersions?: Array<{ status: LESSON_EDITION_STATUSES }>;
  editableVersionsCount?: number;
};

/**
 * Returns the edition status shown in the lessons list.
 * Awaiting validation takes precedence over editing; published is the default.
 * @param lesson Lesson with denormalized edition flags or editable versions.
 * @returns Status matching the list filter values.
 */
export function getLessonListDisplayStatus(lesson: LessonListStatusSource): LESSON_LIST_STATUSES {
  const isAwaitingValidation =
    lesson.hasVersionAwaitingValidation ??
    lesson.editableVersions?.some(
      (version) => version.status === LESSON_EDITION_STATUSES.AWAITING_VALIDATION,
    ) ??
    false;

  if (isAwaitingValidation) {
    return LESSON_LIST_STATUSES.AWAITING_VALIDATION;
  }

  const isEditing =
    lesson.hasVersionEditing ??
    lesson.editableVersions?.some(
      (version) => version.status === LESSON_EDITION_STATUSES.EDITING,
    ) ??
    false;
  const hasEditableVersions = lesson.hasEditableVersions ?? (lesson.editableVersionsCount ?? 0) > 0;

  if (isEditing || hasEditableVersions) {
    return LESSON_LIST_STATUSES.EDITING;
  }

  return LESSON_LIST_STATUSES.PUBLISHED;
}

/**
 * Returns the highest published version of a lesson, if any.
 * @param lesson Lesson with an optional published versions list.
 * @returns Published version number, or null when the lesson has never been published.
 */
export function getLessonPublishedVersion(
  lesson: Pick<LessonListVersionSource, 'versions'>,
): VersionObjectBase<false> | null {
  const latestPublishedVersion = findHighestVersion(lesson.versions);

  if (!latestPublishedVersion) {
    return null;
  }

  return toVersionNumber(latestPublishedVersion);
}

/**
 * Returns the version shown in the lessons list and CSV export.
 * Uses the highest version number. A draft is kept when it is at least as high as the latest published version.
 * @param lesson Lesson with optional editable and published versions.
 * @returns Displayed version number, or null when the lesson has no version yet.
 */
export function getLessonListDisplayedVersion(
  lesson: LessonListVersionSource,
): VersionObjectBase<false> | null {
  const displayedVersion = resolveDisplayedVersion(lesson);

  if (!displayedVersion) {
    return null;
  }

  return toVersionNumber(displayedVersion);
}

/**
 * Returns the lessons-list route token of the displayed version.
 * Drafts use their id. Published versions use `major.minor`.
 * @param lesson Lesson with optional editable and published versions.
 * @returns Route token, or null when the lesson has no version yet.
 */
export function getLessonListVersionKey(lesson: LessonListVersionSource): string | null {
  const latestEditableVersion = findHighestVersion(lesson.editableVersions);
  const displayedVersion = resolveDisplayedVersion(lesson);

  if (!displayedVersion) {
    return null;
  }

  if (displayedVersion === latestEditableVersion && latestEditableVersion?.id) {
    return latestEditableVersion.id;
  }

  return serializeVersion(displayedVersion);
}
